import { useEffect, useMemo } from "react";
import { AlertTriangle, Ban, ExternalLink, Hash, Inbox, Mail, MessageSquare, RefreshCw } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  useSlackChannels,
  useSlackIntegration,
} from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";

type NotificationChannel = "none" | "feed" | "email" | "slack_dm" | "slack_channel";

const CHANNEL_OPTIONS: { value: NotificationChannel; label: string; icon: React.ReactNode }[] = [
  { value: "none", label: "Don't notify", icon: <Ban className="h-3.5 w-3.5" /> },
  { value: "feed", label: "Feed", icon: <Inbox className="h-3.5 w-3.5" /> },
  { value: "email", label: "My email", icon: <Mail className="h-3.5 w-3.5" /> },
  { value: "slack_dm", label: "Slack DM", icon: <MessageSquare className="h-3.5 w-3.5" /> },
  { value: "slack_channel", label: "Slack channel", icon: <Hash className="h-3.5 w-3.5" /> },
];

type NotificationChannelSelectorProps = {
  value: string;
  onChange: (value: string) => void;
  slackChannelId?: string;
  onSlackChannelChange?: (id: string) => void;
};

const NotificationChannelSelector = ({
  value,
  onChange,
  slackChannelId,
  onSlackChannelChange,
}: NotificationChannelSelectorProps) => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id;
  const selected = CHANNEL_OPTIONS.find((o) => o.value === value) ?? CHANNEL_OPTIONS[0];

  const isSlackSelected = value === "slack_channel" || value === "slack_dm";

  const { isError: slackNotConnected, isLoading: slackLoading } = useSlackIntegration({
    workspaceId,
    enabled: !!workspaceId && isSlackSelected,
    retry: false,
  } as any);

  const {
    mutate: fetchChannels,
    data: channelsResponse,
    isLoading: isLoadingChannels,
  } = useSlackChannels();

  useEffect(() => {
    if (workspaceId && value === "slack_channel" && !slackNotConnected) {
      fetchChannels({ workspace_id: workspaceId, refresh: false });
    }
  }, [workspaceId, value, slackNotConnected, fetchChannels]);

  const channels = useMemo(
    () =>
      (channelsResponse?.data?.channels || []).map((ch: any) => ({
        value: ch.id,
        label: `#${ch.normalised_name || ch.name}`,
      })),
    [channelsResponse],
  );

  const connectedAppsUrl = workspaceId
    ? `/home/${workspaceId}/settings/integrations`
    : "#";

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">Notification channel</label>
      <Select value={value || "none"} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-full max-w-[280px]">
          <SelectValue>
            <span className="flex items-center gap-2">
              {selected.icon}
              {selected.label}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {CHANNEL_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <span className="flex items-center gap-2">
                {option.icon}
                {option.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Slack not connected warning */}
      {isSlackSelected && !slackLoading && slackNotConnected && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">Slack is not connected</p>
            <p className="mt-0.5 text-xs text-amber-700">
              Connect your Slack workspace to enable Slack notifications.
            </p>
            <a
              href={connectedAppsUrl}
              className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Open Connected Apps
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}

      {/* Slack DM info */}
      {value === "slack_dm" && !slackNotConnected && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          All meeting attendees who are members of your workspace will receive a Slack DM automatically.
        </p>
      )}

      {/* Slack channel picker */}
      {value === "slack_channel" && !slackNotConnected && (
        <div className="mt-2">
          <label className="text-xs text-muted-foreground">Slack channel</label>
          <div className="mt-1 flex items-center gap-2">
            <Select
              value={slackChannelId || ""}
              onValueChange={(v) => onSlackChannelChange?.(v)}
            >
              <SelectTrigger className="h-8 max-w-[280px] text-sm">
                <SelectValue placeholder={isLoadingChannels ? "Loading..." : "Select channel"} />
              </SelectTrigger>
              <SelectContent>
                {channels.map((ch: any) => (
                  <SelectItem key={ch.value} value={ch.value}>
                    {ch.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => fetchChannels({ workspace_id: workspaceId, refresh: true })}
              title="Refresh channels"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoadingChannels ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationChannelSelector;
